import { useEffect, useState } from 'react';
import { Input } from '../design';
import type { FinancePeriod, FinancePeriodField } from '../lib/finance-summary';

/** Shared selector for complete UTC calendar-month finance/report windows.
 *
 * The inputs are draft-buffered: desktop Firefox renders type="month" as a
 * plain text box (no native picker), so every keystroke produces a partial
 * value like "2026-0". The parent's transition logic rightly refuses to
 * commit partials — but if the input's value came straight from the
 * committed period, each keystroke would snap back and typing would be
 * impossible (Faraz, 2026-09-01). Drafts hold what the user typed; the
 * committed period only advances when a keystroke yields a valid month,
 * and external period changes (including a commit of the typed value)
 * resync the drafts via the effect below. */
export function CompleteMonthRangeControls({
  period,
  error,
  onChange,
  labelPrefix,
}: {
  period: FinancePeriod;
  error: string | null;
  onChange: (field: FinancePeriodField, value: string) => void;
  labelPrefix: string;
}) {
  const from = period.from.slice(0, 7);
  const to = period.to.slice(0, 7);
  const [drafts, setDrafts] = useState({ from, to });
  useEffect(() => {
    setDrafts({ from, to });
  }, [from, to]);
  const errorId = `${labelPrefix.toLowerCase().replace(/\s+/g, '-')}-period-error`;

  const handleChange = (field: FinancePeriodField, value: string) => {
    setDrafts((d) => ({ ...d, [field]: value }));
    onChange(field, value);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        From
        <Input
          type="month"
          inputSize="sm"
          value={drafts.from}
          onChange={(event) => handleChange('from', event.target.value)}
          aria-label={`${labelPrefix} start month`}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-fg-tertiary">
        To
        <Input
          type="month"
          inputSize="sm"
          value={drafts.to}
          onChange={(event) => handleChange('to', event.target.value)}
          aria-label={`${labelPrefix} end month`}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </label>
      <p id={errorId} className="basis-full text-xs text-negative" role="alert" aria-live="polite">
        {error}
      </p>
    </div>
  );
}
